import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { login as apiLogin } from '@/api/endpoints'
import api from '@/api/client'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(localStorage.getItem('jwt_token'))
  const autoLoginAttempted = ref(false)

  const isAuthenticated = computed(() => !!token.value)

  function parsePayload() {
    if (!token.value) return null
    try {
      const base64 = token.value.split('.')[1]
      return JSON.parse(atob(base64))
    } catch {
      return null
    }
  }

  const userEmail = computed(() => parsePayload()?.username ?? null)
  const userId = computed<number | null>(() => parsePayload()?.uid ?? null)
  const userRoles = computed<string[]>(() => parsePayload()?.roles ?? [])
  const isAdmin = computed(() => userRoles.value.includes('ROLE_ADMIN'))
  const allowedApps = computed<string[]>(() => parsePayload()?.apps ?? [])

  function canAccessApp(app: string): boolean {
    return isAdmin.value || allowedApps.value.includes(app)
  }

  async function login(email: string, password: string) {
    const jwt = await apiLogin(email, password)
    token.value = jwt
    localStorage.setItem('jwt_token', jwt)
  }

  async function logout() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore errors — cookie may already be gone
    }
    token.value = null
    localStorage.removeItem('jwt_token')
  }

  function clearAuth() {
    token.value = null
    localStorage.removeItem('jwt_token')
  }

  /**
   * Try to restore session via refresh token cookie.
   * Called once on app startup before router guard kicks in.
   */
  async function tryAutoLogin(): Promise<boolean> {
    if (autoLoginAttempted.value) return isAuthenticated.value
    autoLoginAttempted.value = true

    // If we already have a valid (not expired) JWT, skip refresh
    if (token.value) {
      const payload = parsePayload()
      if (payload?.exp && payload.exp * 1000 > Date.now()) {
        return true
      }
    }

    try {
      const { data } = await api.post('/auth/refresh')
      const jwt = data.token as string
      token.value = jwt
      localStorage.setItem('jwt_token', jwt)
      return true
    } catch {
      token.value = null
      localStorage.removeItem('jwt_token')
      return false
    }
  }

  return {
    token,
    isAuthenticated,
    userEmail,
    userId,
    userRoles,
    isAdmin,
    allowedApps,
    canAccessApp,
    login,
    logout,
    clearAuth,
    tryAutoLogin,
  }
})
